import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import InterviewContent, { type InterviewData, type ImageInfo } from '@/components/online_clinic/examinations/InterviewContent'
import ImageLightbox from '@/components/online_clinic/examinations/ImageLightbox'
import CancelReservationModal from '@/components/online_clinic/examinations/CancelReservationModal'
import ReservationGuideModal from '@/components/online_clinic/examinations/ReservationGuideModal'
import PageHeader from '@/components/common/PageHeader'
import PatientHamburgerMenu from '@/components/common/PatientHamburgerMenu'

type HeaderState = {
  is_past: boolean
  can_start_web_meeting: boolean
  can_edit_interview: boolean
  can_cancel: boolean
}

type ReservationInfo = {
  clinic_name: string
  pharmacy_name: string
  medical_department_name: string
  reserved_at: string
  doctor_full_name: string
  doctor_profile_image_url: string
  doctor_self_introduction: string | null
  patient_full_name: string | null
}

type Downloads = {
  show_prescription: boolean
  show_billing_only: boolean
  show_billing_with_prescription: boolean
  prescription_section_label: string
}

type Paths = {
  edit: string
  cancel: string
  web_meeting: string
  prescription_image: string
  billing_image: string
  root: string
}

// 医療保険資格情報が未登録の場合の自費診療同意 (#1236)
type SelfPay = {
  insurance_card_missing: boolean
  consented: boolean
  consent_path: string
  insurance_card_edit_path: string
}

type Props = {
  header: HeaderState
  reservation_info: ReservationInfo
  interview: InterviewData
  downloads: Downloads
  paths: Paths
  self_pay: SelfPay
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start border-b border-[#e4e4e9] pb-3 last:border-b-0 last:pb-0">
      <dt className="w-[6.5rem] shrink-0 text-[13px] text-[#6f7680] pt-[2px]">{label}</dt>
      <dd className="flex-1 text-[14px] text-[#2a3039]">{children}</dd>
    </div>
  )
}

export default function ExaminationShow({ header, reservation_info, interview, downloads, paths, self_pay }: Props) {
  const [lightboxImage, setLightboxImage] = useState<ImageInfo | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showGuideModal, setShowGuideModal] = useState(false)
  // 自費診療の了承チェック (#1236)。チェックしない限り「診察を始める」は活性化しない
  const [selfPayChecked, setSelfPayChecked] = useState(false)
  const needsSelfPayConsent = self_pay.insurance_card_missing && !self_pay.consented

  const handleCancel = () => {
    router.delete(paths.cancel)
  }

  const title = header.is_past ? '診療詳細' : '予約確認'
  const showDownloads = downloads.show_prescription || downloads.show_billing_only

  return (
    <>
      <Head title={`${title} - オンライン診療`} />
      <div className="min-h-screen bg-[#f4f6f8]">
        <PageHeader trailing={<PatientHamburgerMenu />} subtitle={title} />

        <main className="mx-auto max-w-3xl px-4 py-6 space-y-4">
          {/* 資格情報未登録の案内 (#1236)。同意前のみ表示し、登録導線を出す */}
          {!header.is_past && needsSelfPayConsent && (
            <>
              <div className="rounded-[8px] border border-[#f2d477] bg-[#fff8e6] p-4 text-[13px] leading-relaxed text-[#2a3039]">
                <p className="mb-1 text-[14px] font-bold text-[#9a5b00]">医療保険資格情報が登録されていません</p>
                このまま診察を受ける場合、診療費は<span className="font-bold">全額自己負担（自費診療）</span>
                となります。医療保険資格情報をお持ちの場合は、診察までにご登録ください。
              </div>
              <a
                href={self_pay.insurance_card_edit_path}
                className="block w-full text-center text-[#2196f3] bg-[#dff0fd] rounded-[30px] px-4 py-3 text-[14px] font-medium"
              >
                医療保険資格情報を登録する
              </a>
            </>
          )}

          {/* Action buttons */}
          {!header.is_past && (
            <div className="oc-card space-y-3">
              <p className="text-[13px] text-[#6f7680]">予約時間までに事前準備・利用方法をお読みください</p>
              <button
                type="button"
                onClick={() => setShowGuideModal(true)}
                className="block w-full text-center text-[#2196f3] bg-[#dff0fd] rounded-[30px] px-4 py-3 text-[14px] font-medium"
              >
                事前準備・利用方法
              </button>
              {needsSelfPayConsent ? (
                <>
                  {/* 自費診療の了承チェック。チェック時のみ「診察を始める」を活性化し、押下で同意を記録して入室する (#1236) */}
                  <button
                    type="button"
                    onClick={() => setSelfPayChecked((v) => !v)}
                    aria-pressed={selfPayChecked}
                    className={`flex w-full items-start gap-[10px] rounded-[8px] border p-3 text-left text-[14px] ${
                      selfPayChecked ? 'border-[#2196f3] bg-[#f4fafe]' : 'border-[#d5d6d7] bg-white'
                    }`}
                  >
                    <span
                      className={`mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border-2 text-[14px] font-bold text-white ${
                        selfPayChecked ? 'border-[#2196f3] bg-[#2196f3]' : 'border-[#b9bec4]'
                      }`}
                    >
                      {selfPayChecked ? '✓' : ''}
                    </span>
                    <span>
                      <span className="font-bold">自費診療になることを了承する</span>
                      <span className="mt-[2px] block text-[12px] text-[#6f7680]">
                        医療保険資格情報の登録がないため、診療費は全額自己負担になります
                      </span>
                    </span>
                  </button>
                  <form method="post" action={self_pay.consent_path}>
                    <input
                      type="hidden"
                      name="authenticity_token"
                      value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''}
                    />
                    <button
                      type="submit"
                      disabled={!selfPayChecked || !header.can_start_web_meeting}
                      className="button-primary"
                    >
                      診察を始める
                    </button>
                  </form>
                </>
              ) : header.can_start_web_meeting ? (
                <a href={paths.web_meeting} className="button-primary">
                  診察を始める
                </a>
              ) : (
                <button type="button" disabled className="button-primary">
                  診察を始める
                </button>
              )}
              {self_pay.consented && self_pay.insurance_card_missing && (
                <p className="text-center text-[12px] text-[#9a5b00]">
                  自費診療に同意済みです（医療保険資格情報が未登録のため全額自己負担となります）
                </p>
              )}
              <p className="text-center text-[12px] text-[#6f7680]">※予約5分前になると入れます</p>
            </div>
          )}

          {header.can_edit_interview && (
            <a href={paths.edit} className="button-primary">
              問診内容を修正する
            </a>
          )}

          {/* 予約情報 */}
          <div className="oc-card">
            <h2 className="oc-section-title">予約情報</h2>
            <dl className="space-y-3">
              <InfoRow label="診療機関">{reservation_info.clinic_name}</InfoRow>
              <InfoRow label="薬局名">{reservation_info.pharmacy_name}</InfoRow>
              <InfoRow label="診療科目">{reservation_info.medical_department_name}</InfoRow>
              <InfoRow label="予約日時">{reservation_info.reserved_at}</InfoRow>
              {reservation_info.patient_full_name && (
                <InfoRow label="来院される方">{reservation_info.patient_full_name}さま</InfoRow>
              )}
            </dl>
          </div>

          {/* 担当医師 */}
          <div className="oc-card">
            <h2 className="oc-section-title">担当医師</h2>
            <div className="flex items-start gap-4">
              <img
                src={reservation_info.doctor_profile_image_url}
                alt={reservation_info.doctor_full_name}
                className="w-[80px] h-[80px] rounded-full object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-[#2a3039]">{reservation_info.doctor_full_name}先生</div>
                {reservation_info.doctor_self_introduction && (
                  <p className="text-[12px] text-[#6f7680] mt-1 leading-relaxed">
                    {reservation_info.doctor_self_introduction}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 問診内容 */}
          <div className="oc-card">
            <h2 className="oc-section-title">問診内容</h2>
            <InterviewContent data={interview} onImageClick={setLightboxImage} />
          </div>

          {/* ダウンロード */}
          {showDownloads && (
            <div className="oc-card space-y-3">
              {downloads.show_billing_only ? (
                <>
                  <h2 className="oc-section-title !mb-0">請求書</h2>
                  <a
                    href={paths.billing_image}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button-secondary"
                  >
                    クリニックの請求書
                  </a>
                </>
              ) : (
                <>
                  <h2 className="oc-section-title !mb-0">{downloads.prescription_section_label}</h2>
                  <a
                    href={paths.prescription_image}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button-secondary"
                  >
                    処方せんを確認
                  </a>
                  {downloads.show_billing_with_prescription && (
                    <a
                      href={paths.billing_image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-secondary"
                    >
                      クリニックの請求書
                    </a>
                  )}
                </>
              )}
            </div>
          )}

          <div className="pt-2 space-y-3">
            <a href={paths.root} className="button-primary">
              トップに戻る
            </a>
            {header.can_cancel && (
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                className="block w-full text-center text-[12px] text-[#97979a] underline"
              >
                予約キャンセル
              </button>
            )}
          </div>
        </main>
      </div>

      <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      <CancelReservationModal open={showCancelModal} onClose={() => setShowCancelModal(false)} onConfirm={handleCancel} />
      <ReservationGuideModal open={showGuideModal} onClose={() => setShowGuideModal(false)} />
    </>
  )
}
